/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import dynamic from 'next/dynamic';
import { GetServerSideProps } from 'next';
import FeaturedLastNews from '@components/Home/FeaturedLastNews';
import { api, mimir } from '@/services/api';
import { TimeLine } from '@/types/types';
import { AdContainer } from '@/components/Layout/ItemListLastNews/styles';
import { NavBarContentData, Segmentations, TimeLineApi } from '@/shared/types/home.types';
import { SchemaType } from '@/components/Internals/InternalSchema';
import getConcurrentPromises from '@/utils/getConcurrentPromises';
import Layout from '@/components/Layout';

export type Vitrine = {
  tipo: string;
  titulo: string;
  categoria: string;
  autor: string;
  url: string;
  imagem: {
    url: string;
    blurhash: string;
  };
  data: string;
  idade: string;
};

export type HeadTags = {
  tag: string;
  content: string;
  attr: {
    rel: string;
    href: string;
    type: string | undefined;
    title: string | undefined;
    name: string | undefined;
    content: string | undefined;
    property: string | undefined;
    'http-equiv': string | undefined;
  };
};

export interface NavBarContent {
  data: {
    top: {
      Notícias: {
        tipo: string;
        link: string;
        prefixo: string;
        svg: string;
        links: {
          'Últimas Notícias': string;
          'Mais Lidas': string;
          Smartphone: string;
          Tutoriais: string;
          Apps: string;
          Análises: string;
          Entretenimento: string;
          Games: string;
          Ciência: string;
          Espaço: string;
          Eventos: string;
          Segurança: string;
        };
      };
    };
  };
}

export interface LastNewsItems {
  timeline: {
    itens: TimeLine[];
    paginacao: string;
  };
}

export interface FeaturedNewsType {
  navBarData: any;
  tagsData: HeadTags[];
  homeData?: LastNewsItems;
  timelineData?: LastNewsItems;
  segmentacoes: Segmentations[];
  schema?: SchemaType;
  manualTitle?: string;
  manualRouting?: string;
  routeType?: 'mais' | 'categoria' | 'tipo' | 'tipoDePublicacao' | 'produto' | 'empresa' | 'autor' | 'ultimas';
  route?: string;
}

export interface FeaturedNewsApi {
  data: LastNewsItems;
  ssr: HeadTags[];
  segmentacoes: Segmentations[];
  schema?: SchemaType;
  headers: {
    'Cache-control': string;
    Expires: string;
  };
}

export default function LastNewsRoot({
  navBarData,
  tagsData,
  segmentacoes,
  manualTitle,
  manualRouting,
  timelineData,
  homeData,
  routeType,
  route,
  schema,
}: FeaturedNewsType): JSX.Element {
  const AdsTypeGenerators = dynamic(() => import('@/components/Ads/Google/AdsTypeGenerators'), { ssr: false });

  const title = manualTitle || `Últimas Notícias`;

  return (
    <Layout layoutData={{ segmentacoes, tagsData, navBarData, schema }}>
      <FeaturedLastNews
        featuredLastNewsData={timelineData?.timeline || homeData?.timeline}
        rootPage={manualRouting || 'ultimas'}
        textTitle={title}
        headingTag="h1"
        routeType={routeType || 'ultimas'}
        route={route || '/ultimas'}
      />
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { host } = context.req.headers;

  const mainReq = { promise: api.get<FeaturedNewsApi>(`/ultimas/p1/`), link: `/ultimas/` };
  const timelineReq = { promise: mimir.get<TimeLineApi>(`/timelines/ultimas/`), link: `/timelines/ultimas/` };
  const responseMenuReq = { promise: api.get<NavBarContentData>(`/componentes/menu/`), link: `/componentes/menu/` };

  const { promiseData, data, statusCode } = await getConcurrentPromises({
    mainPromise: mainReq,
    navBarPromise: responseMenuReq,
    timelinePromise: timelineReq,
    host,
  });

  if (data.redirect || data.notFound) return data;

  const navBarData: NavBarContentData = promiseData[responseMenuReq.link].data.data;
  const tagsData = data.ssr;
  const { segmentacoes, schema } = data;
  const timelineData = promiseData[timelineReq.link].data.data;

  context.res.setHeader('Cache-Control', data.headers['Cache-control']);
  context.res.setHeader('Expires', data.headers.Expires);
  context.res.statusCode = statusCode;

  return {
    props: {
      tagsData,
      navBarData,
      segmentacoes,
      schema,
      timelineData,
    },
  };
};
